import React from 'react';
import PersonalInfoStep from 'pages/leadGen/components/PersonalInfoStep';
import VehicleSelectionStep from './components/VehicleSelectionStep';
import BudgetSelectionStep from './components/BudgetSelectionStep';
import CreditScoreSelectionStep from './components/CreditScoreSelectionStep';
import EmploymentStatusStep from './components/EmploymentStatusStep';
import { TFunction } from 'i18next';
import TaxDeductionStep from './components/TaxDeductionStep';
import SecondSourceOfIncomeStep from './components/SecondSourceOfIncomeStep';
import YearsOfIncomeStep from './components/YearsOfIncomeStep';
import WorkInfoStep from './components/WorkInfoStep';
import AddressStep from './components/AddressStep';
import YearsOfLivedStep from './components/YearsOfLivedStep';
import RentOrOwnStep from './components/RentOrOwnStep';
import DobStep from './components/DobStep';
import { StepConfigItem } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import VerifyOtpStep from './components/VerifyOtpStep';

const StepComponentMap: Record<string, JSX.Element> = {
  PersonalInfoStep: <PersonalInfoStep callStepSpecificAction={true} />,
  VehicleSelectionStep: <VehicleSelectionStep callStepSpecificAction={true} />,
  BudgetSelectionStep: <BudgetSelectionStep callStepSpecificAction={true} />,
  CreditScoreSelectionStep: <CreditScoreSelectionStep callStepSpecificAction={true} />,
  EmploymentStatusStep: <EmploymentStatusStep callStepSpecificAction={true} />,
  TaxDeductionStep: <TaxDeductionStep callStepSpecificAction={true} />,
  SecondSourceOfIncomeStep: <SecondSourceOfIncomeStep callStepSpecificAction={true} />,
  YearsOfIncomeStep: <YearsOfIncomeStep callStepSpecificAction={true} />,
  WorkInfoStep: <WorkInfoStep callStepSpecificAction={true} />,
  AddressStep: <AddressStep callStepSpecificAction={true} />,
  YearsOfLivedStep: <YearsOfLivedStep callStepSpecificAction={true} />,
  RentOrOwnStep: <RentOrOwnStep callStepSpecificAction={true} />,
  DobStep: <DobStep callStepSpecificAction={true} />,
  VerifyOtpStep: <VerifyOtpStep callStepSpecificAction={true} />,
};

const getStepMessage = (stepName: string, t: TFunction): string => {
  const messageMap: Record<string, string> = {
    BudgetSelectionStep: t('leadGen.footerTxt1'),
    CreditScoreSelectionStep: t('leadGen.footerTxt2'),
    EmploymentStatusStep: t('leadGen.footerTxt3'),
    TaxDeductionStep: t('leadGen.footerTxt4'),
    SecondSourceOfIncomeStep: t('leadGen.footerTxt5'),
    YearsOfIncomeStep: t('leadGen.footerTxt4'),
    WorkInfoStep: t('leadGen.footerTxt6'),
    AddressStep: t('leadGen.footerTxt7'),
    YearsOfLivedStep: t('leadGen.footerTxt8'),
    RentOrOwnStep: t('leadGen.footerTxt9'),
  };

  return messageMap[stepName] || '';
};

export const generateDynamicStepConfig = (
  widgetData: {
    staffUniqueId: string;
    associateDealerCode: string;
    organizationUniqueId: string;
    stepConfig: string[];
    dealershipId?: string;
  },
  t: TFunction
): StepConfigItem[] => {
  let stepToSubmitAt = '';
  const otpIndex = widgetData?.stepConfig?.indexOf('VerifyOtpStep');

  if (otpIndex > 0) {
    stepToSubmitAt = widgetData?.stepConfig[otpIndex - 1]; // Step before VerifyOtpStep
  } else {
    stepToSubmitAt = widgetData?.stepConfig[widgetData?.stepConfig.length - 1]; // Last step
  }

  const firstStep = widgetData?.stepConfig?.[0];

  return widgetData?.stepConfig?.map((stepName, index) => {
    const isFinalStep = stepName === stepToSubmitAt;
    const isFirstStep = stepName === firstStep;

    return {
      [stepName]: {
        component: React.cloneElement(StepComponentMap[stepName], {
          isSubmit: isFinalStep,
          mappedData: isFirstStep
            ? {
                staffUniqueId: widgetData?.staffUniqueId ?? '',
                associateDealerCode: widgetData?.associateDealerCode ?? '',
                organizationUniqueId: widgetData?.dealershipId || widgetData?.organizationUniqueId,
              }
            : {},
        }),
        message: getStepMessage(stepName, t),
        step: index,

        footerNavigationProps: {
          submitButtonVisibility: otpIndex < 0,
        },
      },
    };
  });
};

export const leadGenStepConfig1 = (t: TFunction): StepConfigItem[] => {
  return [
    {
      step1: {
        component: <PersonalInfoStep callStepSpecificAction={true} />,
        message: '',
        step: 0,
      },
    },
    {
      step2: {
        component: <VehicleSelectionStep callStepSpecificAction={true} />,
        message: '',
        step: 1,
      },
    },
    {
      step3: {
        component: <BudgetSelectionStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt1'),
        step: 2,
      },
    },
    {
      step4: {
        component: <CreditScoreSelectionStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt2'),
        step: 3,
      },
    },
    {
      step5: {
        component: <EmploymentStatusStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt3'),
        step: 4,
      },
    },
    {
      step6: {
        component: <TaxDeductionStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt4'),
        step: 5,
      },
    },
    {
      step7: {
        component: <SecondSourceOfIncomeStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt5'),
        step: 6,
      },
    },
    {
      step8: {
        component: <YearsOfIncomeStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt4'),
        step: 7,
      },
    },
    {
      step9: {
        component: <WorkInfoStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt6'),
        step: 8,
      },
    },
    {
      step10: {
        component: <AddressStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt7'),
        step: 9,
      },
    },
    {
      step11: {
        component: <YearsOfLivedStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt8'),
        step: 10,
      },
    },
    {
      step12: {
        component: <RentOrOwnStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt9'),
        step: 11,
      },
    },
    {
      step13: {
        component: <DobStep callStepSpecificAction={true} />,
        message: '',
        step: 12,
      },
    },
  ];
};

export const leadGenStepConfig2 = (t: TFunction): StepConfigItem[] => {
  return [
    {
      step1: {
        component: <PersonalInfoStep callStepSpecificAction={true} />,
        message: '',
        step: 0,
      },
    },
    {
      step2: {
        component: <VehicleSelectionStep callStepSpecificAction={true} />,
        message: '',
        step: 1,
      },
    },
    {
      step3: {
        component: <EmploymentStatusStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt3'),
        step: 4,
      },
    },
    {
      step4: {
        component: <TaxDeductionStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt4'),
        step: 5,
      },
    },
    {
      step5: {
        component: <AddressStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt7'),
        step: 9,
      },
    },
    {
      step6: {
        component: <YearsOfLivedStep callStepSpecificAction={true} />,
        message: t('leadGen.footerTxt8'),
        step: 10,
      },
    },
    {
      step7: {
        component: <DobStep callStepSpecificAction={false} />,
        message: '',
        step: 12,
      },
    },
  ];
};
